/**
 * Parseo del CSV de alta masiva de alumnos.
 *
 * Formato recomendado: `Nombre;Apellido;Email`. Con cabecera, las columnas se
 * localizan por su nombre y se ignora cualquier otra (departamento, teléfono…).
 * Sin cabecera, el correo se detecta por el `@` y el nombre sale de las dos
 * primeras celdas restantes, así que un CSV de `Nombre;Email` también vale.
 *
 * Los ficheros de los clientes vienen de Excel y traen de todo: BOM, punto y
 * coma como separador, comillas y codificación rota (`ZuÃ±iga` en vez de
 * `Zuñiga`). Aquí se normaliza todo antes de enseñar la previsualización.
 */

export interface ParsedStudent {
  /** Nombre, tal y como viene en el CSV. */
  name: string;
  /** Apellido; vacío si el CSV solo trae una columna de nombre. */
  surname: string;
  email: string;
}

/** Nombre completo para guardar y para saludar en el email. */
export function fullName(student: ParsedStudent): string {
  return [student.name, student.surname].filter(Boolean).join(' ').trim();
}

export interface ParseCsvResult {
  students: ParsedStudent[];
  /** Filas descartadas (sin correo válido o sin nombre), con su contenido original. */
  invalidLines: string[];
  /** Separador detectado, para poder explicarlo en la UI. */
  delimiter: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Quita acentos y pasa a minúsculas, para comparar cabeceras sin sorpresas. */
function normalizeHeader(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

interface HeaderMap {
  name: number;
  surname: number;
  email: number;
}

/**
 * Localiza las columnas por el nombre de la cabecera. Devuelve null si la línea
 * no parece una cabecera (p. ej. ya trae un correo: es una fila de datos).
 */
function readHeader(cells: string[]): HeaderMap | null {
  if (cells.some((c) => c.includes('@'))) return null;

  const map: HeaderMap = { name: -1, surname: -1, email: -1 };
  cells.forEach((cell, i) => {
    const h = normalizeHeader(cell);
    if (map.email === -1 && (h === 'email' || h.startsWith('correo') || h.startsWith('mail')))
      map.email = i;
    else if (map.surname === -1 && h.startsWith('apellido')) map.surname = i;
    else if (map.name === -1 && (h === 'nombre' || h === 'name')) map.name = i;
  });

  return map.email !== -1 && map.name !== -1 ? map : null;
}

/**
 * Devuelve el texto del fichero con la codificación arreglada. Cubre los dos
 * casos que nos hemos encontrado:
 *  - El fichero NO es UTF-8 (lo típico de Excel en Windows): al decodificar
 *    como UTF-8 aparecen caracteres de reemplazo → se reintenta en windows-1252.
 *  - El fichero es UTF-8 doblemente codificado (`Ã±`): se vuelven a tratar sus
 *    code points como bytes y se decodifica otra vez.
 */
export function decodeCsv(buffer: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(buffer);

  if (utf8.includes('\uFFFD')) {
    return stripBom(new TextDecoder('windows-1252').decode(buffer));
  }

  return stripBom(repairDoubleEncoding(utf8));
}

/**
 * Arregla el texto doblemente codificado (`ZuÃ±iga` → `Zuñiga`) secuencia a
 * secuencia, no de una vez: un fichero puede traer alguna secuencia rota suelta
 * (un `Ã` huérfano), y reparar el texto entero fallaría por culpa de ella.
 */
function repairDoubleEncoding(text: string): string {
  return text.replace(/[\u00c2-\u00c3][\u0080-\u00bf]/g, (pair) => {
    const bytes = Uint8Array.from([pair.charCodeAt(0), pair.charCodeAt(1)]);
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    return decoded.includes('\uFFFD') ? pair : decoded;
  });
}

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/**
 * Trocea una línea respetando las comillas: Excel exporta `"Apellido, Nombre"`
 * y partir por el separador a secas se cargaría el nombre.
 */
function splitCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (ch === '"') {
      // Comilla doblada dentro de un campo entrecomillado: es una comilla real.
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

/** El separador más repetido en la primera línea con contenido. */
function detectDelimiter(firstLine: string): string {
  const counts = [';', ',', '\t'].map((d) => ({
    d,
    n: firstLine.split(d).length - 1,
  }));
  const best = counts.sort((a, b) => b.n - a.n)[0]!;
  return best.n > 0 ? best.d : ';';
}

/**
 * Convierte el contenido del CSV en filas {name, email}. No importa el orden de
 * las columnas: la del correo se detecta por el `@`, y el nombre es la otra.
 * La cabecera (si la hay) se descarta sola porque no contiene ningún email.
 */
export function parseStudentsCsv(text: string): ParseCsvResult {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return { students: [], invalidLines: [], delimiter: ';' };

  const delimiter = detectDelimiter(lines[0]!);
  const students: ParsedStudent[] = [];
  const invalidLines: string[] = [];
  const seen = new Set<string>();

  const header = readHeader(splitCsvLine(lines[0]!, delimiter));
  const dataLines = header ? lines.slice(1) : lines;

  for (const line of dataLines) {
    const cells = splitCsvLine(line, delimiter);

    // Con cabecera mandan sus columnas; sin ella, el correo es la celda con @ y
    // el nombre son las dos primeras celdas restantes (nombre y apellido).
    const emailCell = header
      ? (cells[header.email] ?? '')
      : (cells.find((c) => EMAIL_RE.test(c.toLowerCase())) ?? '');

    if (!EMAIL_RE.test(emailCell.toLowerCase())) {
      if (cells.some((c) => c.includes('@'))) invalidLines.push(line);
      continue;
    }

    const email = emailCell.toLowerCase();
    if (seen.has(email)) continue; // duplicado dentro del propio fichero
    seen.add(email);

    // Con cabecera, nombre y apellido salen de sus columnas. Sin ella, las dos
    // primeras celdas que no son el correo: la segunda puede no existir.
    const rest = cells.filter((c) => c !== emailCell && c);
    const name = (header ? cells[header.name] : rest[0]) ?? '';
    const surname = (header ? (header.surname !== -1 ? cells[header.surname] : '') : rest[1]) ?? '';

    if (!name.trim()) {
      invalidLines.push(line); // sin nombre no se puede saludar en el email
      continue;
    }
    students.push({ name: name.trim(), surname: surname.trim(), email });
  }

  return { students, invalidLines, delimiter };
}
